import React, { useEffect, useState } from 'react';
import api from '../api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BackButton from '../components/BackButton';

export default function CashBookPage() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState('Cash');
  const [ledger, setLedger] = useState([]);
  const [opening, setOpening] = useState(0);
  const [closing, setClosing] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/cashbank-accounts').then(res => setAccounts(res.data));
    api.get('/settings/get').then(res => setSettings(res.data || {}));
  }, []);

  useEffect(() => { fetchLedger(); }, [selected]);
  function fetchLedger() {
    const params = [];
    if (from) params.push(`from=${from}`);
    if (to) params.push(`to=${to}`);
    const paramStr = params.length ? `?${params.join('&')}` : '';
    api.get(`/cashbook/${selected}${paramStr}`).then(res => {
      setLedger(res.data.ledger || []);
      setOpening(res.data.opening || 0);
      setClosing(res.data.closing || 0);
    });
  }

  // Company header lines (centered, multi-line)
  const phoneLine = [
    settings.Phone1 ? `Ph: ${settings.Phone1}` : '',
    settings.Phone2 ? `Ph: ${settings.Phone2}` : '',
    settings.Phone3 ? `Ph: ${settings.Phone3}` : ''
  ].filter(Boolean).join('   ');
  const companyLines = [
    settings.BusinessName || '',
    settings.Address || '',
    phoneLine,
    settings.GSTIN ? `GSTIN: ${settings.GSTIN}` : ''
  ].filter(Boolean);

  // Prepare ledger rows with running balance
  let running = Number(opening || 0);
  const allRows = [
    {
      Date: '',
      Particulars: 'Opening Balance',
      Type: '',
      InAmount: running,
      OutAmount: 0,
      Balance: running,
    },
    ...ledger.map(row => {
      running += (Number(row.InAmount) || 0) - (Number(row.OutAmount) || 0);
      return { ...row, Balance: running };
    })
  ];

  // Filtered by search term
  const filteredRows = allRows.filter(row =>
    search.trim() === '' ||
    Object.values(row)
      .join(' ')
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  // Export handlers
  const handleExportExcel = () => {
    const data = filteredRows.map(row => ({
      Date: row.Date ? new Date(row.Date).toLocaleDateString('en-GB') : '',
      Type: row.Type,
      Particulars: row.Particulars,
      'In (₹)': Number(row.InAmount || 0).toFixed(2),
      'Out (₹)': Number(row.OutAmount || 0).toFixed(2),
      Balance: Number(row.Balance || 0).toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CashBook');
    ws['A1'].v = (settings.BusinessName || '') + (settings.Address ? `, ${settings.Address}` : '');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${selected}_CashBook.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(15);

    // Centered company info
    let pdfY = 16;
    companyLines.forEach(line => {
      doc.text(line, doc.internal.pageSize.getWidth() / 2, pdfY, { align: 'center' });
      pdfY += 7;
    });

    doc.setFontSize(12);
    doc.text(`${selected} Book`, doc.internal.pageSize.getWidth() / 2, pdfY, { align: 'center' });
    pdfY += 8;

    if (from || to) {
      doc.setFontSize(10);
      doc.text(
        `Period: ${from || '...'} to ${to || '...'}`,
        doc.internal.pageSize.getWidth() / 2,
        pdfY,
        { align: 'center' }
      );
      pdfY += 6;
    }

    // PDF summary cards (with pastel colors)
    doc.setFontSize(11);

    // Opening Balance box (light blue)
    doc.setFillColor(233, 245, 255); // Light blue
    doc.roundedRect(14, pdfY - 3, 90, 12, 2, 2, 'F');
    doc.setTextColor(33, 37, 41); // Bootstrap dark
    doc.text(`Opening Balance: ₹${Number(opening).toFixed(2)}`, 18, pdfY + 6);

    // Closing Balance box (light green)
    doc.setFillColor(231, 251, 233); // Light green
    doc.roundedRect(110, pdfY - 3, 90, 12, 2, 2, 'F');
    doc.setTextColor(25, 135, 84); // Bootstrap green
    doc.text(`Closing Balance: ₹${Number(closing).toFixed(2)}`, 114, pdfY + 6);

    doc.setTextColor(0,0,0); // Reset for table
    pdfY += 15;

    // Table
    autoTable(doc, {
      startY: pdfY,
      head: [['Date', 'Type', 'Particulars', 'In (₹)', 'Out (₹)', 'Balance']],
      body: filteredRows.map(row => [
        row.Date ? new Date(row.Date).toLocaleDateString('en-GB') : '',
        row.Type,
        row.Particulars,
        Number(row.InAmount || 0).toFixed(2),
        Number(row.OutAmount || 0).toFixed(2),
        Number(row.Balance || 0).toFixed(2),
      ]),
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [0, 123, 255] },
      margin: { left: 14, right: 14 },
      theme: 'grid',
    });

    doc.save(`${selected}_CashBook.pdf`);
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 1100 }}>
      {/* Company info centered */}
      <BackButton></BackButton>
      <div className="text-center mb-2">
        <h4 style={{ fontWeight: 'bold', marginBottom: 4 }}>
          {settings.BusinessName}
        </h4>
        <div style={{ fontSize: 15 }}>
          {settings.Address && <div>{settings.Address}</div>}
          {phoneLine && <div>{phoneLine}</div>}
          {settings.GSTIN && <div>GSTIN: {settings.GSTIN}</div>}
        </div>
      </div>
      <h2 className="text-center mb-4">{selected} Book</h2>

      {/* Summary cards for opening/closing */}
      <div className="row mb-3 justify-content-center">
        <div className="col-6 col-md-3">
          <div className="card shadow-sm border-info mb-2" style={{ background: '#e9f5ff' }}>
            <div className="card-body py-2 text-center">
              <div className="fw-bold" style={{ fontSize: 15 }}>Opening Balance</div>
              <div className="fs-5 text-info">₹{Number(opening).toFixed(2)}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card shadow-sm border-success mb-2" style={{ background: '#e7fbe9' }}>
            <div className="card-body py-2 text-center">
              <div className="fw-bold" style={{ fontSize: 15 }}>Closing Balance</div>
              <div className="fs-5 text-success">₹{Number(closing).toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and export */}
      <div className="d-flex flex-wrap align-items-end mb-3 gap-2">
        <select
          className="form-select w-auto"
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          {accounts.map(ac => (
            <option key={ac.AccountID} value={ac.AccountName}>{ac.AccountName}</option>
          ))}
        </select>
        <label className="ms-2">From:</label>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="form-control w-auto" />
        <label className="ms-2">To:</label>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} className="form-control w-auto" />
        <button className="btn btn-sm btn-primary ms-2" onClick={fetchLedger}>Apply</button>
        <input
          className="form-control w-auto ms-3"
          type="search"
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 160 }}
        />
        <div className="ms-auto">
          <button className="btn btn-outline-success me-2" onClick={handleExportExcel}>Export to Excel</button>
          <button className="btn btn-outline-primary" onClick={handleExportPDF}>Export to PDF</button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-info">
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Particulars</th>
              <th>In (₹)</th>
              <th>Out (₹)</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-muted">No transactions</td>
              </tr>
            )}
            {filteredRows.map((row, i) => (
              <tr key={i}>
                <td>{row.Date ? new Date(row.Date).toLocaleDateString('en-GB') : ''}</td>
                <td>{row.Type}</td>
                <td>{row.Particulars}</td>
                <td>{Number(row.InAmount || 0).toFixed(2)}</td>
                <td>{Number(row.OutAmount || 0).toFixed(2)}</td>
                <td>{Number(row.Balance || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
